import type { BoardingOffer, BoardingServiceWrapper } from 'model/Service';

export const isBoardingDealOfferType = (offer: BoardingOffer): boolean =>
    String(offer.offerType ?? '')
        .toLowerCase()
        .trim() === 'deal';

export const sameBoardingDealOffer = (offer: BoardingOffer, saved: BoardingOffer): boolean => {
    if (!isBoardingDealOfferType(offer)) return false;
    if (saved.uuid && offer.uuid && String(offer.uuid).trim() === String(saved.uuid).trim()) return true;
    if (saved.id == null || offer.id == null) return false;
    return Number(offer.id) === Number(saved.id);
};

/** Injects a saved deal from appointment details when it is missing from fetchBoardingOffersThunk. */
export function mergeSavedBoardingDealIntoServices(
    services: BoardingServiceWrapper[],
    savedOffer?: BoardingOffer,
    preferredServiceId?: number
): BoardingServiceWrapper[] {
    if (!savedOffer || !isBoardingDealOfferType(savedOffer) || preferredServiceId == null) {
        return services;
    }

    return services.map(service => {
        if (service.serviceId !== preferredServiceId) {
            return service;
        }
        const offers = service.offers ?? [];
        if (offers.some(o => sameBoardingDealOffer(o, savedOffer))) {
            return service;
        }
        return { ...service, offers: [...offers, savedOffer] };
    });
}

/** True when the deal is present in the current fetchBoardingOffers catalog for this service. */
export function boardingDealStillAvailable(
    boardingServices: Array<Pick<BoardingServiceWrapper, 'serviceId' | 'offers'>>,
    serviceId: number,
    deal?: BoardingOffer
): boolean {
    if (!deal || !isBoardingDealOfferType(deal)) {
        return false;
    }
    const svc = boardingServices.find(s => s.serviceId === serviceId);
    return (svc?.offers ?? []).some(o => sameBoardingDealOffer(o, deal));
}

export function findBoardingDealInServiceOffers(
    service: Pick<BoardingServiceWrapper, 'offers'>,
    deal: BoardingOffer
): BoardingOffer | undefined {
    return (service.offers ?? []).find(o => sameBoardingDealOffer(o, deal));
}

export function getFirstBoardingDealInCatalog(
    boardingServices: Array<Pick<BoardingServiceWrapper, 'serviceId' | 'offers'>>,
    preferredServiceId?: number
): { serviceId: number; offer: BoardingOffer } | undefined {
    const withDeals = boardingServices.filter(s => (s.offers ?? []).some(isBoardingDealOfferType));
    if (withDeals.length === 0) {
        return undefined;
    }

    const ordered =
        preferredServiceId != null
            ? (() => {
                  const pref = withDeals.find(s => s.serviceId === preferredServiceId);
                  return pref ? [pref, ...withDeals.filter(s => s.serviceId !== preferredServiceId)] : withDeals;
              })()
            : withDeals;

    for (const service of ordered) {
        const offer = (service.offers ?? []).find(isBoardingDealOfferType);
        if (offer) {
            return { serviceId: service.serviceId, offer };
        }
    }

    return undefined;
}

export function filterServicesWithBoardingDeals(services: BoardingServiceWrapper[]): BoardingServiceWrapper[] {
    return services.filter(service => (service.offers ?? []).some(isBoardingDealOfferType));
}
